// Namespace
import * as ProposalNS from '../Proposal';
import { LOCALE, formatLocalizedString, formatMessageWithLocale } from '../../../utils/i18n';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import ProposalOrientation from '@/types/proposal-orientation';
import Locale from '@/types/locale';
import QuestionnaireAnswerItem from '@/types/questionnaire-answer-item';

class ProposalSuitabilityDeterminationSectionBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  rationaleAnswers: QuestionnaireAnswers;

  orientation: ProposalOrientation;

  locale: Locale;

  section: ProposalNS.ProposalTemplateSectionElement;

  constructor(section: ProposalNS.ProposalTemplateSectionElement, rationaleAnswers: QuestionnaireAnswers, orientation: ProposalOrientation, locale: Locale) {
    this.section = section;
    this.rationaleAnswers = rationaleAnswers;
    this.orientation = orientation;
    this.locale = locale;
  }

  splitSuitabilityAnswersIntoPages = (): QuestionnaireAnswerItem[][] => {
    const CHARS_PER_LINE = 140;
    const PIXELS_PER_LINE = 25;
    const PIXELS_PER_PAGE = 1050;
    const ARBITRARY_MARGIN = 100;
    const NOTES_TITLE = PIXELS_PER_LINE + 36;

    const pages: QuestionnaireAnswerItem[][] = [[]]; // Initialize pages list with an empty page

    // Keep track of current page index
    let currentPageIndex: number = 0;
    // Keep track of the % of remaining space on the current page
    let remainingAllocation: number = 100;

    for (const answer of this.rationaleAnswers.answers) {
      let { text, notes } = answer;
      if (!text && answer.options && answer.options.length) {
        text = answer.options.map(opt => formatLocalizedString(opt.description, this.locale)).join(', ');
      }

      if (text || notes) {
        // Fits about 140 chars on a line, one line takes about 25px. Calculate space taken from those values.
        // Yes, this seems extremely arbitrary, and that's because it is - but this is probably the best we can do
        // given the resources available.

        // now that we use html markup we need more space. lets add half a line per <li> and a line per <p>
        const questionText = formatLocalizedString(answer.question.title, this.locale);
        const nbP = (questionText.match(/<p>/g) || []).length;
        const nbUL = (questionText.match(/<ul>/g) || []).length;
        const nbLI = (questionText.match(/<li>/g) || []).length;
        const textSize = questionText.length + nbUL * CHARS_PER_LINE + nbP * CHARS_PER_LINE + nbLI * 0.5 * CHARS_PER_LINE;
        const questionSpace = (textSize / CHARS_PER_LINE) * PIXELS_PER_LINE;
        const answerSpace = text ? Math.ceil(text.length / CHARS_PER_LINE) * PIXELS_PER_LINE : 0;
        const notesTitle = notes ? NOTES_TITLE : 0;
        const notesSpace = notes ? (notes.length / CHARS_PER_LINE) * PIXELS_PER_LINE : 0;
        const pageSpace = ((questionSpace + answerSpace + notesTitle + notesSpace + ARBITRARY_MARGIN) / PIXELS_PER_PAGE) * 100;

        const currentAnswerAllocation = Math.min(pageSpace, 100);

        if (remainingAllocation < currentAnswerAllocation) {
          currentPageIndex++;
          remainingAllocation = 100;
          pages[currentPageIndex] = [];
        }

        pages[currentPageIndex].push(answer);
        remainingAllocation -= currentAnswerAllocation;
      }
    }

    return pages;
  };

  getAnswerTexts = (answer: QuestionnaireAnswerItem) => {
    const texts = [] as ProposalNS.HtmlContentElement[];

    if (answer.text) {
      texts.push({
        content: {
          'en-CA': answer.text,
          'fr-CA': answer.text,
        },
      });
    } else if (answer.options) {
      texts.push({
        content: {
          'en-CA': answer.options.map(opt => opt.description[LOCALE.EN_CA]).join(', '),
          'fr-CA': answer.options.map(opt => opt.description[LOCALE.FR_CA]).join(', '),
        },
      });
    }

    if (answer.notes) {
      texts.push({
        content: {
          'en-CA': formatMessageWithLocale('Comparison.Questionnaire.Notes.Title', this.locale),
          'fr-CA': formatMessageWithLocale('Comparison.Questionnaire.Notes.Title', this.locale),
        },
        className: 'suitability-answer-notes-title',
      });
      texts.push({
        content: {
          'en-CA': answer.notes,
          'fr-CA': answer.notes,
        },
      });
    }

    return texts;
  };

  build = () => {
    const pages = this.splitSuitabilityAnswersIntoPages();

    this.newSections = pages.map(
      (page, index) =>
        ({
          type: 'SECTION',
          key: `SUITABILITY_DETERMINATION SUITABILITY_DETERMINATION_${index}`,
          forced: true,
          buildRules: [
            {
              type: 'PROPOSAL_PROPERTY',
              property: 'rationaleAnswers',
            },
            {
              type: 'PERMISSION',
              permissions: ['rationaleQuestionnaire'],
            },
          ],
          pageFraction: {
            Portrait: 1,
            Landscape: 1,
          },
          label: {
            'en-CA': 'Suitability Determination',
            'fr-CA': "Détermination de l'adéquation",
          },
          sidebarControl: 'CHECKBOX',
          showTitle: true,
          showItemNamesInDisclaimers: true,
          settings: this.section.settings,
          layout: {
            type: 'LAYOUT',
            layout: {
              type: 'GRID',
              columns: 1,
              className: 'suitability-answers-grid',
            },
            elements: page.map(answer => {
              const answerIndex = this.rationaleAnswers.answers.findIndex(a => a._id != null && a._id === answer._id);
              const questionNo = (answerIndex + 1).toString();

              return {
                type: 'LAYOUT',
                layout: {
                  type: 'GRID',
                  columns: 1,
                  className: 'suitability-answer-item',
                },
                elements: [
                  {
                    type: 'LAYOUT',
                    layout: {
                      type: 'GRID',
                      columns: 2,
                      className: 'suitability-answer-title-row',
                    },
                    elements: [
                      {
                        type: 'DATA',
                        dataView: {
                          type: 'TEXT',
                          className: 'question-no',
                        },
                        DAO: {
                          type: 'TEXT',
                          localizedTextDAOOptions: {
                            texts: [
                              {
                                text: {
                                  'en-CA': questionNo,
                                  'fr-CA': questionNo,
                                },
                              },
                            ],
                          },
                        },
                      },
                      {
                        type: 'DATA',
                        dataView: {
                          type: 'HTML',
                          className: 'suitability-question',
                        },
                        DAO: {
                          type: 'HTML_CONTENT',
                          htmlContentDAOOptions: {
                            html: [
                              {
                                content: answer.question.title,
                              },
                            ],
                          },
                        },
                      },
                    ],
                  },
                  {
                    type: 'LAYOUT',
                    layout: {
                      type: 'GRID',
                      columns: 2,
                      className: 'suitability-answer-text-row',
                    },
                    elements: [
                      {
                        type: 'DATA',
                        dataView: {
                          type: 'TEXT',
                        },
                        DAO: {
                          type: 'TEXT',
                          localizedTextDAOOptions: {
                            texts: [
                              {
                                text: {
                                  'en-CA': '',
                                  'fr-CA': '',
                                },
                              },
                            ],
                          },
                        },
                      },
                      {
                        type: 'DATA',
                        dataView: {
                          type: 'HTML',
                        },
                        DAO: {
                          type: 'HTML_CONTENT',
                          htmlContentDAOOptions: {
                            html: this.getAnswerTexts(answer),
                          },
                        },
                      },
                    ],
                  },
                ],
              };
            }),
          },
          sectionType: 'QUESTIONNAIRE',
        }) as ProposalNS.ProposalTemplateSectionElement,
    );
  };
}

export const buildSuitabilityDeterminationSections = (
  section: ProposalNS.ProposalTemplateSectionElement,
  rationaleAnswers: QuestionnaireAnswers | undefined,
  orientation: ProposalOrientation,
  locale: Locale,
): ProposalNS.ProposalTemplateSectionElement[] => {
  if (rationaleAnswers == null) {
    return [];
  }

  const builder = new ProposalSuitabilityDeterminationSectionBuilder(section, rationaleAnswers, orientation, locale);
  builder.build();

  return builder.newSections;
};
